import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { useCreateTechProfile } from '@/hooks/use-crew';
import { usePlatformModules } from '@/hooks/use-modules';
import { usePermissions } from '@/hooks/use-permissions';
import { handleApiError } from '@/lib/api-client';
import type { CreateTechProfilePayload, EmploymentType, TechProfile } from '@/lib/types';

interface EmergencyContactForm {
  name: string;
  relationship: string;
  phone: string;
  secondary_phone: string;
  email: string;
}

type TechPrefillField =
  | 'first_name'
  | 'last_name'
  | 'email'
  | 'phone'
  | 'address'
  | 'city'
  | 'state_province'
  | 'country'
  | 'postal_code';

export interface TechProfileFormProps {
  /** Link the new tech profile to an existing person record. */
  personId?: number;
  initialValues?: Partial<Record<TechPrefillField, string>>;
  onCreated: (tech: TechProfile) => void;
  /** Rendered next to the submit button (e.g. a Cancel link). */
  cancelAction?: React.ReactNode;
}

/**
 * The Add Technician form, shared by Crew → Technicians → Add Technician and
 * the onboarding wizard's technician step.
 */
export function TechProfileForm({
  personId,
  initialValues = {},
  onCreated,
  cancelAction,
}: TechProfileFormProps) {
  const createMutation = useCreateTechProfile();
  const { isModuleEnabled } = usePlatformModules();
  const permissions = usePermissions();
  const canManagePayRates =
    permissions.isAdmin || permissions.hasMinModuleRole('payroll', 'manager');
  const hasH3Pay = isModuleEnabled('payroll');

  const [form, setForm] = useState<CreateTechProfilePayload>(() => ({
    ...(personId ? { person_id: personId } : {}),
    first_name: initialValues.first_name ?? '',
    last_name: initialValues.last_name ?? '',
    email: initialValues.email ?? '',
    phone: initialValues.phone ?? '',
    secondary_phone: '',
    date_of_birth: '',
    address: initialValues.address ?? '',
    city: initialValues.city ?? '',
    state_province: initialValues.state_province ?? '',
    country: initialValues.country ?? '',
    postal_code: initialValues.postal_code ?? '',
    hire_date: '',
    job_title: '',
    employment_type: 'full_time',
    notes: '',
  }));

  const [contacts, setContacts] = useState<EmergencyContactForm[]>([]);
  const [error, setError] = useState<string | null>(null);

  const updateField = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const addContact = () => {
    setContacts((prev) => [
      ...prev,
      { name: '', relationship: '', phone: '', secondary_phone: '', email: '' },
    ]);
  };

  const updateContact = (index: number, field: keyof EmergencyContactForm, value: string) => {
    setContacts((prev) =>
      prev.map((c, i) => (i === index ? { ...c, [field]: value } : c))
    );
  };

  const removeContact = (index: number) => {
    setContacts((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!personId && (!form.first_name || !form.last_name || !form.email)) {
      setError('First name, last name, and email are required.');
      return;
    }

    try {
      const formData = { ...form };
      if (personId) {
        const personFields = [
          'first_name', 'last_name', 'email', 'phone', 'secondary_phone',
          'address', 'city', 'state_province', 'country', 'postal_code',
        ] as const;
        for (const field of personFields) {
          if (!formData[field]) {
            delete formData[field];
          }
        }
      }
      const payload: CreateTechProfilePayload = {
        ...formData,
        date_of_birth: formData.date_of_birth || undefined,
        hire_date: formData.hire_date || undefined,
        emergency_contacts: contacts.filter((c) => c.name && c.phone),
      };
      const result = await createMutation.mutateAsync(payload);
      onCreated(result);
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError.message);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Personal Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="first_name">First Name *</Label>
              <Input
                id="first_name"
                value={form.first_name}
                onChange={(e) => updateField('first_name', e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="last_name">Last Name *</Label>
              <Input
                id="last_name"
                value={form.last_name}
                onChange={(e) => updateField('last_name', e.target.value)}
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="email">{personId ? 'Email' : 'Email *'}</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => updateField('email', e.target.value)}
                required={!personId}
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={form.phone}
                onChange={(e) => updateField('phone', e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="secondary_phone">Secondary Phone</Label>
              <Input
                id="secondary_phone"
                value={form.secondary_phone}
                onChange={(e) => updateField('secondary_phone', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="date_of_birth">Date of Birth</Label>
              <Input
                id="date_of_birth"
                type="date"
                value={form.date_of_birth}
                onChange={(e) => updateField('date_of_birth', e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="address">Address</Label>
            <Input
              id="address"
              value={form.address}
              onChange={(e) => updateField('address', e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                value={form.city}
                onChange={(e) => updateField('city', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="state_province">State / Province</Label>
              <Input
                id="state_province"
                value={form.state_province}
                onChange={(e) => updateField('state_province', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="country">Country</Label>
              <Input
                id="country"
                value={form.country}
                onChange={(e) => updateField('country', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="postal_code">Postal Code</Label>
              <Input
                id="postal_code"
                value={form.postal_code}
                onChange={(e) => updateField('postal_code', e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Employment Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="job_title">Job Title</Label>
              <Input
                id="job_title"
                value={form.job_title}
                onChange={(e) => updateField('job_title', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="employment_type">Employment Type</Label>
              <Select
                value={form.employment_type}
                onValueChange={(v) => updateField('employment_type', v as EmploymentType)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full_time">Full Time</SelectItem>
                  <SelectItem value="part_time">Part Time</SelectItem>
                  <SelectItem value="contractor">Contractor</SelectItem>
                  <SelectItem value="subcontractor">Subcontractor</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="hire_date">Hire Date</Label>
              <Input
                id="hire_date"
                type="date"
                value={form.hire_date}
                onChange={(e) => updateField('hire_date', e.target.value)}
              />
            </div>
          </div>
          {hasH3Pay && canManagePayRates && (
            <p className="text-sm text-gray-600" data-testid="pay-rates-note">
              Pay rates are managed in{' '}
              <Link to="/payroll/rates" className="text-blue-600 hover:underline">
                Pay → Pay Rates
              </Link>
              . Add this person&apos;s rate there once the profile is created.
            </p>
          )}
          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={form.notes}
              onChange={(e) => updateField('notes', e.target.value)}
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Emergency Contacts</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={addContact}>
              <Plus className="h-4 w-4 mr-1" />
              Add Contact
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {contacts.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">
              No emergency contacts added yet
            </p>
          ) : (
            contacts.map((contact, index) => (
              <div key={index} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">Contact {index + 1}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeContact(index)}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Name</Label>
                    <Input
                      value={contact.name}
                      onChange={(e) => updateContact(index, 'name', e.target.value)}
                      placeholder="Full name"
                    />
                  </div>
                  <div>
                    <Label>Relationship</Label>
                    <Input
                      value={contact.relationship}
                      onChange={(e) => updateContact(index, 'relationship', e.target.value)}
                      placeholder="e.g. Spouse, Parent"
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={contact.phone}
                      onChange={(e) => updateContact(index, 'phone', e.target.value)}
                      placeholder="Phone number"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      value={contact.email}
                      onChange={(e) => updateContact(index, 'email', e.target.value)}
                      placeholder="Email address"
                      type="email"
                    />
                  </div>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <div className="flex gap-3">
        <Button type="submit" disabled={createMutation.isPending}>
          {createMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Create Technician
        </Button>
        {cancelAction}
      </div>
    </form>
  );
}
